import React from 'react';
import { handleError } from '../../lib/errors/handleError';
import { secureLog } from '../../lib/errors/logger';

type Props = { children: React.ReactNode };

export class AppErrorBoundary extends React.Component<Props, { hasError: boolean }> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }
  
  static getDerivedStateFromError() { 
    return { hasError: true }; 
  }
  
  componentDidCatch(error: any, info: any) {
    // Suppress most errors in error boundary to prevent user confusion
    const toSafe = (s: string) => (typeof s === 'string' ? s.replace(/[<>"'&]/g, '').toLowerCase() : '');
    const errorMessage = toSafe(error?.message || '');
    const isSuppressibleError = errorMessage.includes('index') || 
                               errorMessage.includes('permission') || 
                               errorMessage.includes('network') ||
                               errorMessage.includes('unexpected') ||
                               errorMessage.includes('error') ||
                               error?.code === 'DB_INDEX_REQUIRED' ||
                               error?.code === 'TRANSIENT_BACKEND_UNAVAILABLE' ||
                               error?.code === 'UNKNOWN';
    
    if (!isSuppressibleError) {
      handleError(error, { 
        feature: 'render', 
        action: 'render', 
        extra: { info } 
      }, undefined, { notify: false });
    } else {
      secureLog('warn', 'Suppressed error in error boundary:', toSafe(error?.message || 'unknown'));
    }
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
          <div className="max-w-md w-full text-center">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚠️</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                Something went wrong
              </h2>
              <p className="text-gray-600 mb-6">
                An unexpected error occurred. Please refresh the page to try again.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}



